import { X } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { GradeBadge } from './GradeBadge';
import type { PayGradeData, InterviewIQData, InvestorIQData, FundingIQData, Grade } from '@/types';

export function GradeDrawer() {
  const {
    gradeDrawerOpen,
    gradeDrawerCompanyId,
    gradeDrawerCompanyName,
    gradeDrawerType,
    recruiterIntelData,
    closeGradeDrawer,
  } = useAppStore();

  if (!gradeDrawerOpen) return null;

  const intel = recruiterIntelData[gradeDrawerCompanyId];
  const gradeData = intel?.[gradeDrawerType as keyof typeof intel];

  const gradeLabels: Record<string, string> = {
    paygrade: 'PayGrade Analysis',
    interviewiq: 'InterviewIQ Analysis',
    investoriq: 'InvestorIQ Analysis',
    fundingiq: 'FundingIQ Analysis',
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={closeGradeDrawer} />
      <div className="fixed top-0 right-0 w-[600px] max-w-[95vw] h-screen bg-card-dark border-l border-border-dark z-50 overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-bold text-white">{gradeDrawerCompanyName}</h3>
              <p className="text-sm text-gray-400">{gradeLabels[gradeDrawerType]}</p>
              {'gradedAt' in (gradeData || {}) && (
                <p className="text-xs text-gray-500 mt-1">
                  Graded: {new Date((gradeData as { gradedAt: string }).gradedAt).toLocaleString()}
                </p>
              )}
            </div>
            <button onClick={closeGradeDrawer} className="text-gray-400 hover:text-white p-1">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            {!gradeData?.data ? (
              <p className="text-gray-500">No grade data available</p>
            ) : gradeDrawerType === 'paygrade' ? (
              <PayGradeContent data={gradeData.data as PayGradeData} />
            ) : gradeDrawerType === 'interviewiq' ? (
              <InterviewIQContent data={gradeData.data as InterviewIQData} />
            ) : gradeDrawerType === 'investoriq' ? (
              <InvestorIQContent data={gradeData.data as InvestorIQData} />
            ) : gradeDrawerType === 'fundingiq' ? (
              <FundingIQContent data={gradeData.data as FundingIQData} />
            ) : (
              <pre className="text-xs text-gray-300 overflow-auto">{JSON.stringify(gradeData.data, null, 2)}</pre>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function GradeScoreHeader({ grade, score }: { grade: Grade; score: number }) {
  return (
    <div className="bg-gray-900 rounded-lg p-4 text-center">
      <div className="text-6xl font-bold mb-2">
        <GradeBadge grade={grade} size="lg" />
      </div>
      <div className="text-2xl text-gray-400">{score}/100</div>
    </div>
  );
}

function CategoryBreakdown({ categories }: { categories: Array<{ key: string; item: { score: number; grade: Grade; summary: string } | undefined }> }) {
  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h4 className="font-semibold text-emerald-400 mb-3">Breakdown</h4>
      <div className="space-y-2">
        {categories.map(({ key, item }) => {
          if (!item) return null;
          return (
            <div key={key}>
              <div className="flex items-center justify-between border-b border-gray-700 pb-2">
                <span className="text-white capitalize">{key.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-2">
                  <GradeBadge grade={item.grade} />
                  <span className="text-xs text-gray-400">{item.score}/5</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-2">{item.summary}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FlagsList({ title, items, color }: { title: string; items: string[]; color: 'green' | 'red' | 'yellow' }) {
  if (!items?.length) return null;
  const colors = {
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
  };
  const icons = {
    green: '✅',
    red: '🚩',
    yellow: '⚠️',
  };
  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h4 className={`font-semibold ${colors[color]} mb-2`}>{icons[color]} {title}</h4>
      <ul className="text-sm text-gray-300 space-y-1">
        {items.map((item, idx) => (
          <li key={idx}>• {item}</li>
        ))}
      </ul>
    </div>
  );
}

function PayGradeContent({ data }: { data: PayGradeData }) {
  return (
    <>
      <GradeScoreHeader grade={data.overall_grade} score={data.overall_score} />
      
      {data.whats_in_it_for_me && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h4 className="font-semibold text-emerald-400 mb-3">What's In It For You</h4>
          <p className="text-gray-200">{data.whats_in_it_for_me}</p>
        </div>
      )}

      <CategoryBreakdown
        categories={[
          { key: 'compensation', item: data.compensation },
          { key: 'benefits', item: data.benefits },
          { key: 'product', item: data.product },
          { key: 'culture', item: data.culture },
          { key: 'stability', item: data.stability },
        ]}
      />

      <FlagsList title="Green Flags" items={data.green_flags || []} color="green" />
      <FlagsList title="Red Flags" items={data.red_flags || []} color="red" />
    </>
  );
}

function InterviewIQContent({ data }: { data: InterviewIQData }) {
  return (
    <>
      <GradeScoreHeader grade={data.overall_grade} score={data.overall_score} />

      {data.interview_difficulty && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h4 className="font-semibold text-cyan-400 mb-3">Interview Difficulty</h4>
          <div className="text-xl text-white">{data.interview_difficulty.level}</div>
          <p className="text-sm text-gray-400">{data.interview_difficulty.notes}</p>
        </div>
      )}

      <CategoryBreakdown
        categories={[
          { key: 'candidate_experience', item: data.candidate_experience },
          { key: 'process_efficiency', item: data.process_efficiency },
          { key: 'hiring_stability', item: data.hiring_stability },
          { key: 'recruiter_quality', item: data.recruiter_quality },
        ]}
      />

      {data.interview_tips?.length && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h4 className="font-semibold text-blue-400 mb-2">💡 Interview Tips</h4>
          <ul className="text-sm text-gray-300 space-y-1">
            {data.interview_tips.map((tip, idx) => (
              <li key={idx}>• {tip}</li>
            ))}
          </ul>
        </div>
      )}

      {data.common_questions?.length && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-2">❓ Common Questions</h4>
          <ul className="text-sm text-gray-300 space-y-1">
            {data.common_questions.map((q, idx) => (
              <li key={idx}>• {q}</li>
            ))}
          </ul>
        </div>
      )}

      <FlagsList title="Green Flags" items={data.green_flags || []} color="green" />
      <FlagsList title="Red Flags" items={data.red_flags || []} color="red" />
    </>
  );
}

function InvestorIQContent({ data }: { data: InvestorIQData }) {
  return (
    <>
      <GradeScoreHeader grade={data.overall_grade} score={data.overall_score} />

      <CategoryBreakdown
        categories={[
          { key: 'market_position', item: data.market_position },
          { key: 'financial_health', item: data.financial_health },
          { key: 'leadership_quality', item: data.leadership_quality },
          { key: 'growth_potential', item: data.growth_potential },
          { key: 'competitive_advantage', item: data.competitive_advantage },
        ]}
      />

      <FlagsList title="Green Flags" items={data.green_flags || []} color="green" />
      <FlagsList title="Red Flags" items={data.red_flags || []} color="red" />
      <FlagsList title="Risk Factors" items={data.risk_factors || []} color="yellow" />
    </>
  );
}

function FundingIQContent({ data }: { data: FundingIQData }) {
  return (
    <>
      <GradeScoreHeader grade={data.overall_grade} score={data.overall_score} />

      <CategoryBreakdown
        categories={[
          { key: 'funding_history', item: data.funding_history },
          { key: 'burn_rate', item: data.burn_rate },
          { key: 'investor_quality', item: data.investor_quality },
          { key: 'market_timing', item: data.market_timing },
          { key: 'exit_potential', item: data.exit_potential },
        ]}
      />

      <FlagsList title="Green Flags" items={data.green_flags || []} color="green" />
      <FlagsList title="Red Flags" items={data.red_flags || []} color="red" />
      <FlagsList title="Risk Factors" items={data.risk_factors || []} color="yellow" />
    </>
  );
}
